'use client';

import React, { useState } from 'react';
import { reportsAPI, exportAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { handleExport } from '../utils/exportHelper';
import '../styles/Reports.css';

function Reports() {
  const { success, error: showError } = useToast();
  const [reportType, setReportType] = useState('monthly');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleGenerateReport = async () => {
    setLoading(true);
    setReport(null);

    try {
      let response;
      if (reportType === 'monthly') {
        response = await reportsAPI.getMonthlySummary(month);
      } else if (reportType === 'yearly') {
        response = await reportsAPI.getYearlySummary(year);
      }
      setReport(response.data.report);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to generate report';
      showError(message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = () => {
    if (reportType === 'monthly') {
      handleExport(
        () => exportAPI.exportMonthlyReport(month),
        `monthly_report_${month}.xlsx`,
        () => success('Monthly report exported successfully'),
        (error) => showError(error)
      );
    } else if (reportType === 'yearly') {
      handleExport(
        () => exportAPI.exportYearlyReport(year),
        `yearly_report_${year}.xlsx`,
        () => success('Yearly report exported successfully'),
        (error) => showError(error)
      );
    }
  };

  return (
    <div className="reports-container">
      <h1>Reports</h1>

      <div className="report-selector">
        <div className="form-group">
          <label>Report Type</label>
          <select value={reportType} onChange={(e) => setReportType(e.target.value)}>
            <option value="monthly">Monthly Summary</option>
            <option value="yearly">Yearly Summary</option>
          </select>
        </div>

        {reportType === 'monthly' && (
          <div className="form-group">
            <label>Select Month</label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </div>
        )}

        {reportType === 'yearly' && (
          <div className="form-group">
            <label>Select Year</label>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              min="2020"
              max={new Date().getFullYear()}
            />
          </div>
        )}

        <button onClick={handleGenerateReport} className="btn">
          Generate Report
        </button>
        {report && (
          <button onClick={handleExportReport} className="btn btn-secondary">
            📥 Export to Excel
          </button>
        )}
      </div>

      {loading && <div className="loading-spinner">Generating report...</div>}

      {report && (
        <div className="report-container">
          <div className="report-header">
            <h2>
              {reportType === 'monthly'
                ? `Report for ${month}`
                : `Annual Report ${year}`}
            </h2>
          </div>

          <div className="report-cards">
            <div className="report-card income-card">
              <h3>Total Income</h3>
              <p className="value">₹{report.totalIncome?.toFixed(2) || '0.00'}</p>
              {report.numberOfEvents !== undefined && (
                <small>{report.numberOfEvents} events</small>
              )}
            </div>

            <div className="report-card expense-card">
              <h3>Event Expenses</h3>
              <p className="value">₹{report.totalEventExpenses?.toFixed(2) || '0.00'}</p>
            </div>

            <div className="report-card salary-card">
              <h3>Salary Paid</h3>
              <p className="value">₹{report.totalSalaryPaid?.toFixed(2) || '0.00'}</p>
            </div>

            <div className="report-card total-expense-card">
              <h3>Total Expenses</h3>
              <p className="value">₹{report.totalExpenses?.toFixed(2) || '0.00'}</p>
            </div>

            <div className={`report-card profit-card ${report.netProfitOrLoss >= 0 ? 'positive' : 'negative'}`}>
              <h3>Net Profit/Loss</h3>
              <p className="value">₹{report.netProfitOrLoss?.toFixed(2) || '0.00'}</p>
              <small>{report.netProfitOrLoss >= 0 ? 'Profit' : 'Loss'}</small>
            </div>
          </div>

          <div className="report-breakdown">
            <h3>Financial Breakdown</h3>
            <div className="breakdown-items">
              <div className="breakdown-item">
                <span>Income:</span>
                <strong>₹{report.totalIncome?.toFixed(2) || '0.00'}</strong>
              </div>
              <div className="breakdown-item divider"></div>
              <div className="breakdown-item">
                <span>Event Expenses:</span>
                <strong>-₹{report.totalEventExpenses?.toFixed(2) || '0.00'}</strong>
              </div>
              <div className="breakdown-item">
                <span>Salary Expenses:</span>
                <strong>-₹{report.totalSalaryPaid?.toFixed(2) || '0.00'}</strong>
              </div>
              <div className="breakdown-item divider"></div>
              <div className="breakdown-item total">
                <span>Net Result:</span>
                <strong className={report.netProfitOrLoss >= 0 ? 'positive' : 'negative'}>
                  ₹{report.netProfitOrLoss?.toFixed(2) || '0.00'}
                </strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && !report && (
        <div className="no-data">
          Select report type and click "Generate Report" to view your financial summary.
        </div>
      )}
    </div>
  );
}

export default Reports;
